import Autocomplete from "@mui/material/Autocomplete"
import Checkbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import FormHelperText from "@mui/material/FormHelperText"
import FormLabel from "@mui/material/FormLabel"
import { TextField } from "components/text-field"
import { makeStyles } from "tss-react/mui-compat"
import React from "react"
import { Controller, Control } from "react-hook-form"

const useStyles = makeStyles()(
  (theme) => ({
    formControl: {
      display: "flex",
    },
    formLabel: {
      zIndex: 2,
      marginBottom: "8px",
      padding: "0 12px",
      fontFamily: "Lato, sans-serif",
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "20px",
      opacity: 1,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    autocomplete: {
      "& .MuiOutlinedInput-root": {
        padding: "4px 8px",
      },
    },
    formError: {
      display: "flex",
      padding: "12px 0",
    },
  }),
)

type Option = { value: string; label: string }

type IControlledAutocompleteMulti = {
  control: Control
  name: string
  options: Array<Option>
  label?: string
  error?: boolean
  required?: boolean
  showRequired?: boolean
  fieldErrors?: any
  defaultValue?: Array<boolean>
  placeholder?: string
}

// Drop-in replacement for ControlledCheckboxGroup that renders as a typeahead
// (MUI Autocomplete in `multiple` mode). The wire format on the form value is
// the same: an array of booleans matching the option index, so the existing
// payload assembly in createPayloadFromFormQuestions keeps working.
export const ControlledAutocompleteMulti = (props: IControlledAutocompleteMulti) => {
  const {
    control,
    name,
    options,
    label: groupLabel,
    fieldErrors = { message: "Required field." },
    error,
    required = false,
    showRequired = true,
    defaultValue: defaultValues,
    placeholder = "Type to search...",
  } = props

  const { classes } = useStyles()
  const initialValue: boolean[] = defaultValues ?? options.map(() => false)

  return (
    <FormControl error={error} component="fieldset" className={classes.formControl}>
      <FormLabel
        aria-label={required ? `${groupLabel} required` : groupLabel}
        component="legend"
        className={classes.formLabel}>
        {required && showRequired ? `${groupLabel} *` : groupLabel}
      </FormLabel>
      <Controller
        control={control}
        name={name}
        defaultValue={initialValue}
        render={({ field }) => {
          const checks: boolean[] = Array.isArray(field.value)
            ? field.value
            : initialValue
          const selected = options.filter((_, i) => checks[i])

          return (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={options}
              value={selected}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              getOptionLabel={(opt) => opt.label}
              className={classes.autocomplete}
              onChange={(_event, newSelected) => {
                const selectedValues = new Set(newSelected.map((o) => o.value))
                const nextChecks = options.map((o) => selectedValues.has(o.value))
                field.onChange(nextChecks)
              }}
              renderOption={(renderProps, option, { selected: isSelected }) => (
                <li {...renderProps} key={option.value}>
                  <Checkbox checked={isSelected} style={{ marginRight: 8 }} />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selected.length === 0 ? placeholder : undefined}
                  error={error}
                  margin="normal"
                  fieldErrors={fieldErrors}
                />
              )}
            />
          )
        }}
      />
      {error && (
        <FormHelperText error={error} className={classes.formError}>
          {fieldErrors.message}
        </FormHelperText>
      )}
    </FormControl>
  )
}
